import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send } from "lucide-react";

function sid() {
  let s = localStorage.getItem("ask_vytali_sid");
  if (!s) { s = crypto.randomUUID(); localStorage.setItem("ask_vytali_sid", s); }
  return s;
}

const AskVytali: React.FC<{ page?: string }> = ({ page }) => {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<{ role: string; text: string }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "assistant", text: fr
        ? "Bonjour 👋 Je suis Ask Vytali.\nComment puis-je vous aider à découvrir Vytali Academy ?"
        : "Hello 👋 I am Ask Vytali.\nHow can I help you discover Vytali Academy?" }]);
    }
  }, [open, fr, msgs.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const token = localStorage.getItem("vytali_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ask/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: text, session_id: sid(), page, lang: i18n.language }),
      });
      const reader = res.body!.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const l of lines) {
          if (!l.startsWith("data: ")) continue;
          const d = l.slice(6);
          if (d === "[DONE]") continue;
          try {
            const tk = JSON.parse(d).t;
            setMsgs((m) => {
              const c = [...m];
              c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + tk };
              return c;
            });
          } catch {}
        }
      }
    } catch {
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: fr ? "Service momentanément indisponible." : "Service temporarily unavailable." };
        return c;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div data-testid="ask-vytali-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-lg shadow-2xl border border-[#B99341]/40 overflow-hidden flex flex-col" style={{ height: "480px" }}>
          <div className="bg-[#0A1F44] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/brand/emblem.svg" alt="" className="h-8 w-auto" />
              <div>
                <p className="text-white font-semibold text-sm">Ask Vytali</p>
                <p className="text-[#D4B45A] text-[10px] tracking-wide">{fr ? "Votre guide intelligent dans l'écosystème Vytali." : "Your intelligent guide in the Vytali ecosystem."}</p>
              </div>
            </div>
            <button data-testid="ask-vytali-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X size={17} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F7F5F0]">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-[#0A1F44] text-white" : "bg-white border border-[#B99341]/30 text-[#0A1F44]"}`}>
                {m.text || "…"}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-2.5 border-t border-border flex gap-2 bg-white">
            <input data-testid="ask-vytali-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} disabled={busy}
              placeholder={fr ? "Posez votre question…" : "Ask your question…"}
              className="flex-1 px-3 py-2 border border-border rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B99341]" />
            <button data-testid="ask-vytali-send" onClick={send} disabled={busy || !input.trim()}
              className="px-3 bg-[#B99341] text-[#0A1F44] rounded-md hover:bg-[#D4B45A] transition-colors duration-200 disabled:opacity-50"><Send size={16} /></button>
          </div>
        </div>
      )}
      <button data-testid="ask-vytali-button" onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-4 sm:right-6 z-50 flex items-center gap-2 px-4 py-3 bg-[#0A1F44] text-white rounded-full shadow-xl border border-[#B99341] hover:bg-[#132B5C] transition-colors duration-200">
        <MessageCircle size={18} className="text-[#D4B45A]" />
        <span className="text-sm font-medium">Ask Vytali</span>
      </button>
    </>
  );
};

export default AskVytali;
